/**
 * @fileoverview Section engine for the masked date/time editor (SCADatetimeInputInline)
 *
 * Splits a date-fns display pattern (the user's configured format, precision
 * already applied) into independently editable sections (day, month, year,
 * hours, …) plus literal separators, and provides the pure logic the editor
 * needs: digit-typing with auto-advance, arrow stepping with wrap-around,
 * completeness checks, and composing the section values back into a `Date`
 * with deterministic defaults for parts the precision omits (missing month →
 * January, missing day → 1st, missing time → 00:00:00).
 *
 * Everything here is pure and DOM-free so the typing behaviour is unit-testable.
 */

import { getDaysInMonth } from "date-fns";

export type SectionType = "day" | "month" | "year" | "hours" | "minutes" | "seconds" | "milliseconds" | "meridiem";

/** Textual month rendering: `MMM` → abbreviated ("Jul"), `MMMM`+ → full ("July"). */
export type MonthStyle = "short" | "long";

export interface FormatSection {
  kind: "section";
  type: SectionType;
  /** Original date-fns token (e.g. `dd`, `yyyy`, `h`). */
  token: string;
  /** Zero-pad width used for display. */
  padLength: number;
  /** Maximum digits the user can type into the section. */
  maxDigits: number;
  /** True for 12-hour clock tokens (`h`/`hh`) — bounds become 1–12 and a meridiem applies. */
  twelveHour: boolean;
  /**
   * Set for textual month tokens (`MMM`/`MMMM`): the section shows a localized
   * month name and can be typed by name; absent means a numeric month.
   */
  monthStyle?: MonthStyle;
}

export interface FormatLiteral {
  kind: "literal";
  text: string;
}

export type FormatPart = FormatSection | FormatLiteral;

/** Values per section. `null` / absent means "not entered yet". Meridiem: 0 = AM, 1 = PM. */
export type SectionValues = Partial<Record<SectionType, number | null>>;

export interface SectionBounds {
  min: number;
  max: number;
}

const TOKEN_TYPES: Record<string, SectionType> = {
  d: "day",
  M: "month",
  y: "year",
  H: "hours",
  h: "hours",
  m: "minutes",
  s: "seconds",
  S: "milliseconds",
  a: "meridiem",
};

const DATE_SECTION_TYPES: readonly SectionType[] = ["day", "month", "year"];
/** Time sections in significance order — a filled set must form a prefix of this list. */
const TIME_ORDER: readonly SectionType[] = ["hours", "minutes", "seconds", "milliseconds"];

function makeSection(type: SectionType, token: string): FormatSection {
  const twelveHour = type === "hours" && token.startsWith("h");
  let padLength: number;
  let maxDigits: number;
  let monthStyle: MonthStyle | undefined;
  if (type === "meridiem") {
    padLength = 2;
    maxDigits = 0; // set via A/P keys or arrows, not digits
  } else if (type === "milliseconds") {
    // Always a 3-digit fractional-second section (0–999), matching `Date.getMilliseconds()`.
    padLength = 3;
    maxDigits = 3;
  } else if (type === "year") {
    // `yy` stays a strict 2-digit section; anything else edits the full year.
    padLength = token.length === 2 ? 2 : 4;
    maxDigits = padLength;
  } else if (type === "month" && token.length >= 3) {
    // Textual month (`MMM` → abbreviated, `MMMM`+ → full): rendered as a localized
    // name and typed by name, but digit entry (1–12) still works.
    monthStyle = token.length >= 4 ? "long" : "short";
    padLength = 2;
    maxDigits = 2;
  } else {
    padLength = Math.min(token.length, 2);
    maxDigits = 2;
  }
  return { kind: "section", type, token, padLength, maxDigits, twelveHour, monthStyle };
}

/**
 * Tokenize a date-fns pattern into editable sections and literal separators.
 * Repeated sections of the same type keep only their first occurrence editable
 * (patterns in this app never repeat a component). Quoted literals (`'...'`)
 * are unwrapped.
 */
export function parseFormatParts(pattern: string): FormatPart[] {
  const parts: FormatPart[] = [];
  const pushLiteral = (text: string) => {
    if (!text) return;
    const last = parts[parts.length - 1];
    if (last?.kind === "literal") last.text += text;
    else parts.push({ kind: "literal", text });
  };

  let i = 0;
  while (i < pattern.length) {
    const ch = pattern[i];
    if (ch === "'") {
      // date-fns quoted literal, with '' as an escaped quote
      let j = i + 1;
      let text = "";
      while (j < pattern.length) {
        if (pattern[j] === "'") {
          if (pattern[j + 1] === "'") {
            text += "'";
            j += 2;
            continue;
          }
          break;
        }
        text += pattern[j];
        j += 1;
      }
      pushLiteral(text || "'");
      i = j + 1;
      continue;
    }
    const type = TOKEN_TYPES[ch];
    if (!type) {
      pushLiteral(ch);
      i += 1;
      continue;
    }
    let j = i;
    while (j < pattern.length && pattern[j] === ch) j += 1;
    parts.push(makeSection(type, pattern.slice(i, j)));
    i = j;
  }
  return parts;
}

export function formatSections(parts: FormatPart[]): FormatSection[] {
  return parts.filter((p): p is FormatSection => p.kind === "section");
}

/**
 * Min/max for a section. The day maximum tightens once month (and year) are
 * known; with an unknown year February permits 29 — the strict check happens
 * in {@link composeDate} once everything is entered.
 */
export function getSectionBounds(section: FormatSection, values: SectionValues = {}): SectionBounds {
  switch (section.type) {
    case "day": {
      const month = values.month ?? null;
      if (month == null) return { min: 1, max: 31 };
      // 2000 is a leap year → permissive Feb 29 while the year is unknown.
      return { min: 1, max: getDaysInMonth(new Date(values.year ?? 2000, month - 1)) };
    }
    case "month":
      return { min: 1, max: 12 };
    case "year":
      return section.maxDigits === 2 ? { min: 0, max: 99 } : { min: 1, max: 9999 };
    case "hours":
      return section.twelveHour ? { min: 1, max: 12 } : { min: 0, max: 23 };
    case "minutes":
    case "seconds":
      return { min: 0, max: 59 };
    case "milliseconds":
      return { min: 0, max: 999 };
    case "meridiem":
      return { min: 0, max: 1 };
  }
}

export interface TypeDigitResult {
  value: number;
  /** Digits typed so far in the current editing burst. */
  typed: number;
  /** Section can't take another digit — the editor should advance. */
  complete: boolean;
}

/**
 * Apply one typed digit to a section (MUI-style): digits extend the current
 * burst while the result stays within bounds, otherwise the digit starts a
 * fresh value. `complete` signals auto-advance (max digits reached, or no
 * further digit could keep the value valid).
 */
export function typeDigit(
  prevValue: number | null,
  prevTyped: number,
  digit: number,
  bounds: SectionBounds,
  maxDigits: number,
): TypeDigitResult {
  const continuing = prevTyped > 0 && prevTyped < maxDigits && prevValue != null;
  let value = (continuing ? prevValue : 0) * 10 + digit;
  let typed = continuing ? prevTyped + 1 : 1;
  if (value > bounds.max) {
    value = digit;
    typed = 1;
  }
  const complete = typed >= maxDigits || value * 10 > bounds.max;
  return { value, typed, complete };
}

/** Step a section value by ±1, wrapping within bounds. From empty: up → min, down → max. */
export function stepValue(value: number | null, delta: 1 | -1, bounds: SectionBounds): number {
  if (value == null) return delta > 0 ? bounds.min : bounds.max;
  const span = bounds.max - bounds.min + 1;
  return bounds.min + ((((value - bounds.min + delta) % span) + span) % span);
}

export function clampToBounds(value: number, bounds: SectionBounds): number {
  return Math.min(bounds.max, Math.max(bounds.min, value));
}

/**
 * POSIX-style two-digit-year window: 69–99 → 19xx, 0–68 → 20xx.
 * Applied when the user leaves a year section with a value under 100
 * (`01.05.26` → 2026, `26.03.85` → 1985 — spec: short years are forgiven).
 */
export function expandTwoDigitYear(value: number): number {
  return value + (value >= 69 ? 1900 : 2000);
}

export type CompletionState = "empty" | "partial" | "complete";

/**
 * Whether the entered sections form a committable value.
 *
 * - all sections empty → `empty` (the field is cleared);
 * - visible *date* sections must all be filled;
 * - visible *time* sections may be left empty from the least significant end
 *   (typing only a date into a date-and-time field, or only hours into a
 *   minute-precision time — omitted parts default to 0);
 * - a meridiem needs its hours (and vice versa nothing — AM is the default).
 */
export function getCompletionState(sections: FormatSection[], values: SectionValues): CompletionState {
  const types = sections.map((s) => s.type);
  const dateTypes = types.filter((t) => DATE_SECTION_TYPES.includes(t));
  const timeTypes = TIME_ORDER.filter((t) => types.includes(t));

  const dateFilled = dateTypes.filter((t) => values[t] != null).length;
  const timeFilledFlags = timeTypes.map((t) => values[t] != null);
  const meridiemFilled = types.includes("meridiem") && values.meridiem != null;

  const anyFilled = dateFilled > 0 || timeFilledFlags.some(Boolean) || meridiemFilled;
  if (!anyFilled) return "empty";
  if (dateFilled < dateTypes.length) return "partial";

  // Time sections must form a prefix: hours before minutes before seconds.
  const firstEmpty = timeFilledFlags.indexOf(false);
  if (firstEmpty !== -1 && timeFilledFlags.slice(firstEmpty).some(Boolean)) return "partial";
  // Meridiem alone (or with an empty hours section) is not a time.
  if (timeTypes.includes("hours") && values.hours == null && meridiemFilled) return "partial";
  // A pure time field needs at least its hours.
  if (dateTypes.length === 0 && timeTypes.includes("hours") && values.hours == null) return "partial";
  return "complete";
}

export interface ComposeContext {
  /** Supplies the calendar day for formats without date sections (pure time fields). */
  baseDate?: Date;
}

/**
 * Compose a `Date` from entered section values. Parts missing from the format
 * (or left empty at the forgiving end of the time group) get deterministic
 * defaults: month → January, day → 1st, time parts → 0. Returns `null` when
 * the combination is impossible (e.g. 31 February) — that is surfaced by the
 * editor after editing finishes, per the existing validation rules.
 */
export function composeDate(sections: FormatSection[], values: SectionValues, ctx: ComposeContext = {}): Date | null {
  const has = (t: SectionType) => sections.some((s) => s.type === t);
  const hasAnyDate = has("year") || has("month") || has("day");
  const base = ctx.baseDate ?? new Date();

  let year: number;
  if (values.year != null) {
    year = values.year < 100 ? expandTwoDigitYear(values.year) : values.year;
  } else {
    year = base.getFullYear();
  }
  const month = values.month ?? (hasAnyDate ? 1 : base.getMonth() + 1);
  const day = values.day ?? (hasAnyDate ? 1 : base.getDate());

  let hours = values.hours ?? 0;
  const hourSection = sections.find((s) => s.type === "hours");
  if (hourSection?.twelveHour) {
    hours = (hours % 12) + (values.meridiem === 1 ? 12 : 0);
  }
  const minutes = values.minutes ?? 0;
  const seconds = values.seconds ?? 0;
  const milliseconds = values.milliseconds ?? 0;

  const date = new Date(year, month - 1, day, hours, minutes, seconds, milliseconds);
  // `Date` auto-maps years 0–99 to 1900+ — restore the intended year.
  if (year >= 0 && year < 100) date.setFullYear(year);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

/** Extract section values from a `Date` for the given format sections. */
export function dateToSectionValues(date: Date, sections: FormatSection[]): SectionValues {
  const values: SectionValues = {};
  for (const section of sections) {
    switch (section.type) {
      case "day":
        values.day = date.getDate();
        break;
      case "month":
        values.month = date.getMonth() + 1;
        break;
      case "year":
        values.year = section.maxDigits === 2 ? date.getFullYear() % 100 : date.getFullYear();
        break;
      case "hours": {
        const h = date.getHours();
        values.hours = section.twelveHour ? h % 12 || 12 : h;
        if (section.twelveHour) values.meridiem = h >= 12 ? 1 : 0;
        break;
      }
      case "minutes":
        values.minutes = date.getMinutes();
        break;
      case "seconds":
        values.seconds = date.getSeconds();
        break;
      case "milliseconds":
        values.milliseconds = date.getMilliseconds();
        break;
      case "meridiem":
        values.meridiem = date.getHours() >= 12 ? 1 : 0;
        break;
    }
  }
  return values;
}

/**
 * Render a section value for display (zero-padded; meridiem as AM/PM). Textual
 * month sections render the localized name from `monthNames` (1-based month →
 * `monthNames[value - 1]`); pass the array matching the section's `monthStyle`.
 */
export function formatSectionValue(section: FormatSection, value: number, monthNames?: string[]): string {
  if (section.type === "meridiem") return value === 1 ? "PM" : "AM";
  if (section.type === "month" && section.monthStyle && monthNames) {
    return monthNames[value - 1] ?? String(value).padStart(section.padLength, "0");
  }
  return String(value).padStart(section.padLength, "0");
}

export interface MonthMatch {
  /** 1-based month index of the first name matching the query. */
  month: number;
  /** True when exactly one month matches — the editor can auto-advance. */
  complete: boolean;
}

/** Case- and diacritic-insensitive key for prefix matching (`März` → `marz`). */
function monthMatchKey(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/**
 * Match a typed prefix against localized month names. Returns the first month
 * whose name starts with the query (case- and diacritic-insensitive) and whether
 * that match is unique. Returns `null` when the query matches no month.
 */
export function matchMonthName(query: string, monthNames: string[]): MonthMatch | null {
  const key = monthMatchKey(query.trim());
  if (!key) return null;
  const matches: number[] = [];
  monthNames.forEach((name, i) => {
    if (monthMatchKey(name).startsWith(key)) matches.push(i + 1);
  });
  if (matches.length === 0) return null;
  return { month: matches[0], complete: matches.length === 1 };
}
